import { useState, useEffect } from 'react';
import './profilequestionnaire.css';
import axios from 'axios';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';

const ProfileQuestionnaire = ({ onProgressChange, progressCount, maxCount }) => {

    //Variablen für die einzelnen Fragen
    const { t } = useTranslation();
    const [relationshipState, setRelationshipState] = useState(null);
    const [searchingFor, setSearchingFor] = useState(null);
    const [sexuality, setSexuality] = useState(null);
    const [values, setValues] = useState([]);
    const [educationalQualification, setEducationalQualification] = useState(null);
    const [employmentType, setEmploymentType] = useState(null);
    const [mainJob, setMainJob] = useState(null);
    const [outgoing, setOutgoing] = useState(null);
    const [couchpotato, setCouchpotato] = useState(null);
    const [personalEducation, setPersonalEducation] = useState(null);
    const [hobbies, setHobbies] = useState([]);
    const [eveningBook, setEveningBook] = useState(null);
    const [eveningCulture, setEveningCulture] = useState(null);
    const [eveningFoodOrder, setEveningFoodOrder] = useState(null);
    const [eveningRestaurant, setEveningRestaurant] = useState(null);
    const [eveningCocking, setEveningCocking] = useState(null);
    const [eveningOutgoing, setEveningOutgoing] = useState(null);
    const [eveningHussling, setEveningHussling] = useState(null);
    const [alcohol, setAlcohol] = useState(null);
    const [smoking, setSmoking] = useState(null);
    const [children, setChildren] = useState(null);
    const [animals, setAnimals] = useState(null);
    const [favoriteMusic, setFavoriteMusic] = useState([]);

    // Booleans für die Mehr Anzeigen Buttons
    let [showMoreMid, setShowMoreMid] = useState(false);
    let [showMoreBottom, setShowMoreBottom] = useState(false);

    // Dropdown states for custom dropdowns
    const [childrenDropdownOpen, setChildrenDropdownOpen] = useState(false);
    const [animalsDropdownOpen, setAnimalsDropdownOpen] = useState(false);

    // Variablen für die Fortschrittsanzeige
    // Variablen für Block 1
    const [basicProgress, setBasicProgress] = useState(0);
    const [basicMax, setBasicMax] = useState(4);
    // Variablen für Block 2
    const [careerProgress, setCareerProgress] = useState(0);
    const [careerMax, setCareerMax] = useState(3);
    // Variablen für Block 3
    const [leisureProgress, setLeisureProgress] = useState(0);
    const [leisureMax, setLeisureMax] = useState(4);
    // Variablen für Block 4
    const [personalProgress, setPersonalProgress] = useState(0);
    const [personalMax, setPersonalMax] = useState(5);
    // Variablen für Block 5
    const [eveningProgress, setEveningProgress] = useState(0);
    const [eveningMax, setEveningMax] = useState(7);

    //Variablen für die Bilder im questionnaire
    const [greenCheck, setGreenCheckSrc] = useState(null);
    const [redCross, setRedCrossSrc] = useState(null);
    const [plainCheck, setPlainCheckSrc] = useState(null);

    useEffect(() => {
        const filled =
            basicProgress +
            careerProgress +
            leisureProgress +
            personalProgress;

        const total =
            basicMax +
            careerMax +
            leisureMax +
            personalMax;

        // report to parent
        onProgressChange(filled, total);
    }, [basicProgress, careerProgress, leisureProgress, personalProgress]);

    useEffect(() => {
        const fetchQuestionnaireData = async () => {
            try {
                // 🔐 JWT decides who the user is
                const res = await api.get("/user/getquestionnaire");

                const backendData = res.data || {};

                /* -------------------------
                   SET STATE FROM BACKEND
                -------------------------- */
                setRelationshipState(backendData.beziehungsstand ?? null);
                setSearchingFor(backendData.absicht ?? null);
                setSexuality(backendData.gesinnung ?? null);
                setValues(backendData.werte ?? []);
                setEducationalQualification(backendData.bildungsstand ?? null);
                setEmploymentType(backendData.anstellungsart ?? null);
                setMainJob(backendData.hauptberufbranche ?? null);

                setOutgoing(backendData.outgoing ?? null);
                setCouchpotato(backendData.couchpotato ?? null);
                setPersonalEducation(backendData.weiterbildungen ?? null);
                setHobbies(backendData.hobbiesv2 ?? []);

                setEveningBook(backendData.eveningbook ?? null);
                setEveningCulture(backendData.eveningculture ?? null);
                setEveningFoodOrder(backendData.eveningfoodorder ?? null);
                setEveningRestaurant(backendData.eveningrestaurant ?? null);
                setEveningCocking(backendData.eveningcocking ?? null);
                setEveningOutgoing(backendData.eveningoutgoing ?? null);
                setEveningHussling(backendData.eveninghussling ?? null);

                setAlcohol(backendData.trinken ?? null);
                setSmoking(backendData.rauchen ?? null);
                setChildren(backendData.kinder ?? null);
                setAnimals(backendData.haustiere ?? null);
                setFavoriteMusic(backendData.lieblingsmusik ?? []);

                /* -------------------------
                   PROGRESS CALCULATION
                -------------------------- */
                const basicAnswers = [
                    backendData.beziehungsstand,
                    backendData.absicht,
                    backendData.gesinnung,
                    backendData.werte?.length ? "filled" : null
                ];

                const careerAnswers = [
                    backendData.bildungsstand,
                    backendData.anstellungsart,
                    backendData.hauptberufbranche
                ];

                const freetimeAnswers = [
                    backendData.outgoing,
                    backendData.couchpotato,
                    backendData.weiterbildungen,
                    backendData.hobbiesv2?.length ? "filled" : null
                ];

                const eveningAnswers = [
                    backendData.eveningbook,
                    backendData.eveningculture,
                    backendData.eveningfoodorder,
                    backendData.eveningrestaurant,
                    backendData.eveningcocking,
                    backendData.eveningoutgoing,
                    backendData.eveninghussling
                ];

                const personalAnswers = [
                    backendData.trinken,
                    backendData.rauchen,
                    backendData.kinder,
                    backendData.haustiere,
                    backendData.lieblingsmusik?.length ? "filled" : null
                ];

                const countFilled = (arr) =>
                    arr.filter((v) => v !== null && v !== "").length;

                setBasicProgress(countFilled(basicAnswers));
                setCareerProgress(countFilled(careerAnswers));
                setLeisureProgress(countFilled(freetimeAnswers));
                setEveningProgress(countFilled(eveningAnswers));
                setPersonalProgress(countFilled(personalAnswers));
            } catch (err) {
                if (err.response?.status === 401) {
                    console.warn("User not authenticated – questionnaire blocked");
                } else {
                    console.error("Error fetching questionnaire data:", err);
                }
            }
        };

        fetchQuestionnaireData();
    }, []);

    useEffect(() => {
        let basicProgress = 0;
        let leisureProgress = 0;
        let carreerProgress = 0;
        let eveningProgress = 0;
        let privatProgress = 0;

        if (relationshipState) basicProgress += 1;
        if (searchingFor) basicProgress += 1;
        if (sexuality) basicProgress += 1;
        if (values.length > 0) basicProgress += 1;

        setBasicProgress(basicProgress);

        if (educationalQualification) carreerProgress += 1;
        if (employmentType) carreerProgress += 1;
        if (mainJob) carreerProgress += 1;

        setCareerProgress(carreerProgress);

        if (hobbies.length > 0) leisureProgress += 1;
        if (couchpotato !== null && couchpotato !== undefined) leisureProgress += 1;
        if (outgoing !== null && outgoing !== undefined) leisureProgress += 1;
        if (personalEducation !== null && personalEducation !== undefined) leisureProgress += 1;

        setLeisureProgress(leisureProgress);

        if (eveningBook !== null && eveningBook !== undefined) eveningProgress += 1;
        if (eveningCulture !== null && eveningCulture !== undefined) eveningProgress += 1;
        if (eveningFoodOrder !== null && eveningFoodOrder !== undefined) eveningProgress += 1;
        if (eveningRestaurant !== null && eveningRestaurant !== undefined) eveningProgress += 1;
        if (eveningCocking !== null && eveningCocking !== undefined) eveningProgress += 1;
        if (eveningOutgoing !== null && eveningOutgoing !== undefined) eveningProgress += 1;
        if (eveningHussling !== null && eveningHussling !== undefined) eveningProgress += 1;

        setEveningProgress(eveningProgress);

        if (alcohol !== null && alcohol !== undefined) privatProgress += 1;
        if (smoking !== null && smoking !== undefined) privatProgress += 1;
        if (children) privatProgress += 1;
        if (animals) privatProgress += 1;
        if (favoriteMusic.length > 0) privatProgress += 1;

        setPersonalProgress(privatProgress);

    }, [relationshipState, searchingFor, sexuality, values, educationalQualification, employmentType, mainJob, hobbies,
        couchpotato, outgoing, personalEducation, eveningBook, eveningCulture, eveningFoodOrder, eveningRestaurant, eveningCocking, eveningOutgoing, eveningHussling,
        alcohol, smoking, children, animals, favoriteMusic]);

    // Bilder für den Questionnaire einfügen
    useEffect(() => {
        const fetchPictures = async () => {
            try {
                // 🔹 Hardcoded IDs for each image (adjust to your DB)
                const [greenId, redId, plainId] = [15, 16, 17];

                // Call all three routes in parallel
                const [greenRes, redRes, plainRes] = await Promise.all([
                    api.get(`/user/getwebsitepicture/${greenId}`),
                    api.get(`/user/getwebsitepicture/${redId}`),
                    api.get(`/user/getwebsitepicture/${plainId}`)
                ]);

                setGreenCheckSrc(greenRes.data.picture);
                setRedCrossSrc(redRes.data.picture);
                setPlainCheckSrc(plainRes.data.picture);
            } catch (err) {
                console.error("Error fetching one or more pictures:", err);
            }
        };

        fetchPictures();
    });

    const handleSelection = (question, choice) => {
        if (question === "relationshipState") {
            setRelationshipState(choice);
        } else if (question === "searchingFor") {
            setSearchingFor(choice);
        } else if (question === "sexuality") {
            setSexuality(choice);
        } else if (question === "values" || question === "hobbies" || question === "favoriteMusic") {
            const setter = question === "values" ? setValues : question === "hobbies" ? setHobbies : setFavoriteMusic;
            setter(prevValues => {
                const updatedValues = prevValues.includes(choice)
                    ? prevValues.filter(value => value !== choice)
                    : prevValues.length < 3
                        ? [...prevValues, choice]
                        : prevValues;
                return updatedValues;
            });
        } else if (question === "educationalQualification") {
            setEducationalQualification(choice);
        } else if (question === "employmentType") {
            setEmploymentType(choice);
        } else if (question === "mainJob") {
            setMainJob(choice);
        } else if (question === "eveningBook") {
            setEveningBook(choice);
        } else if (question === "eveningCulture") {
            setEveningCulture(choice);
        } else if (question === "eveningFoodOrder") {
            setEveningFoodOrder(choice);
        } else if (question === "eveningRestaurant") {
            setEveningRestaurant(choice);
        } else if (question === "eveningCocking") {
            setEveningCocking(choice);
        } else if (question === "eveningOutgoing") {
            setEveningOutgoing(choice);
        } else if (question === "eveningHussling") {
            setEveningHussling(choice);
        } else if (question === "couchpotato") {
            setCouchpotato(choice);
        } else if (question === "outgoing") {
            setOutgoing(choice);
        } else if (question === "personalEducation") {
            setPersonalEducation(choice);
        } else if (question === "alcohol") {
            setAlcohol(choice);
        } else if (question === "smoking") {
            setSmoking(choice);
        } else if (question === "children") {
            setChildren(choice);
        } else if (question === "animals") {
            setAnimals(choice);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const questionnaireData = {
                beziehungsstand: relationshipState,
                absicht: searchingFor,
                gesinnung: sexuality,
                werte: values,
                bildungsstand: educationalQualification,
                anstellungsart: employmentType,
                hauptberufbranche: mainJob,
                outgoing,
                couchpotato,
                weiterbildungen: personalEducation,
                hobbiesv2: hobbies,
                eveningbook: eveningBook,
                eveningculture: eveningCulture,
                eveningfoodorder: eveningFoodOrder,
                eveningrestaurant: eveningRestaurant,
                eveningcocking: eveningCocking,
                eveningoutgoing: eveningOutgoing,
                eveninghussling: eveningHussling,
                trinken: alcohol,
                rauchen: smoking,
                kinder: children,
                haustiere: animals,
                lieblingsmusik: favoriteMusic
            };

            await api.put("/user/updatequestionnaire", questionnaireData);
            console.log("Questionnaire updated successfully");
        } catch (err) {
            console.error("Error updating questionnaire:", err);
        }
    };

    return (
        <div className="profile-mid">
            <div className="fragebogen-wrapper">
                <h3 style={{ textAlign: 'center' }}>{t('ProfileQuestionnaireTitle')}</h3>
                {!showMoreMid && (
                    <div className="pre-fragebogen">
                        {(basicProgress + leisureProgress + personalProgress + careerProgress) !==
                            (basicMax + careerMax + leisureMax + personalMax) ? (
                            <>
                                {t('ProfileQuestionnaireCheck')}
                            </>
                        ) : (
                            <>🎉 {t('ProfileQuestionnaireComplete')} 🎉</>
                        )}
                    </div>
                )}
                {showMoreMid && (
                    <div className="fragebogen-ges">
                        <details className="fragebogen-part-wrapper" id="fragebogen-part-1"
                            style={{
                                '--icon-red': redCross ? `url(${redCross})` : '',
                                '--icon-green': greenCheck ? `url(${greenCheck})` : '',
                            }}>
                            <summary className={`summary-fragen ${basicProgress === basicMax ? "summary-gruen" : "summary-rot"}`}>{t('ProfileQuestionnaireBasicQuestions')} ({basicProgress}/{basicMax})</summary>
                            <div className="fragen-wrapper">
                                <div className="frage">
                                    <p> {t('ProfileQuestionnaireBasicQuestions1')}</p>
                                    <div className="onclick-boxes" id="relationship_state">
                                        {[t('ProfileQuestionnaireBasicQuestions1_1'), t('ProfileQuestionnaireBasicQuestions1_2'), t('ProfileQuestionnaireBasicQuestions1_3'),
                                        t('ProfileQuestionnaireBasicQuestions1_4'), t('ProfileQuestionnaireBasicQuestions1_5')].map((option, index) => (
                                            <div key={index} className={`type ${relationshipState === index + 1 ? 'selected' : ''}`} onClick={() => handleSelection('relationshipState', index + 1)}>{option}</div>
                                        ))}
                                    </div>
                                </div>
                                <div className="frage">
                                    <p> {t('ProfileQuestionnaireBasicQuestions2')}</p>
                                    <div className="onclick-boxes" id="searching_for">
                                        {[t('ProfileQuestionnaireBasicQuestions2_1'), t('ProfileQuestionnaireBasicQuestions2_2'), t('ProfileQuestionnaireBasicQuestions2_3'),
                                        t('ProfileQuestionnaireBasicQuestions2_4'), t('ProfileQuestionnaireBasicQuestions2_5')].map((option, index) => (
                                            <div key={index} className={`type ${searchingFor === index + 1 ? 'selected' : ''}`} onClick={() => handleSelection('searchingFor', index + 1)}>{option}</div>
                                        ))}
                                    </div>
                                </div>
                                <div className="frage">
                                    <p> {t('ProfileQuestionnaireBasicQuestions3')}</p>
                                    <div className="onclick-boxes" id="sexuality">
                                        {[t('ProfileQuestionnaireBasicQuestions3_1'), t('ProfileQuestionnaireBasicQuestions3_2'), t('ProfileQuestionnaireBasicQuestions3_3')].map((option, index) => (
                                            <div key={index} className={`type ${sexuality === index + 1 ? 'selected' : ''}`} onClick={() => handleSelection('sexuality', index + 1)}>{option}</div>
                                        ))}
                                    </div>
                                </div>
                                <div className="frage">
                                    <p> {t('ProfileQuestionnaireBasicQuestions4')}</p>
                                    <div className="onclick-boxes" id="values">
                                        {[t('ProfileQuestionnaireBasicQuestions4_1'), t('ProfileQuestionnaireBasicQuestions4_2'), t('ProfileQuestionnaireBasicQuestions4_3'),
                                        t('ProfileQuestionnaireBasicQuestions4_4'), t('ProfileQuestionnaireBasicQuestions4_5'), t('ProfileQuestionnaireBasicQuestions4_6'),
                                        t('ProfileQuestionnaireBasicQuestions4_7'), t('ProfileQuestionnaireBasicQuestions4_8'), t('ProfileQuestionnaireBasicQuestions4_9'),
                                        t('ProfileQuestionnaireBasicQuestions4_10'), t('ProfileQuestionnaireBasicQuestions4_11')].map((option, index) => (
                                            <div key={index} className={`type ${values.includes(index + 1) ? 'selected' : ''}`} onClick={() => handleSelection('values', index + 1)}>{option}</div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </details>
                        <details className="fragebogen-part-wrapper" id="fragebogen-part-2"
                            style={{
                                '--icon-red': redCross ? `url(${redCross})` : '',
                                '--icon-green': greenCheck ? `url(${greenCheck})` : '',
                            }}>
                            <summary className={`summary-fragen ${careerProgress === careerMax ? "summary-gruen" : "summary-rot"}`}>{t('ProfileQuestionnaireCareerAndCareerPath')} ({careerProgress}/{careerMax})</summary>
                            <div class="fragen-wrapper">
                                <div className="frage">
                                    <p> {t('ProfileQuestionnaireCareerAndCareerPath1')}</p>
                                    <div className="onclick-boxes" id="educational_qualification">
                                        {[t('ProfileQuestionnaireCareerAndCareerPath1_1'), t('ProfileQuestionnaireCareerAndCareerPath1_2'), t('ProfileQuestionnaireCareerAndCareerPath1_3'),
                                        t('ProfileQuestionnaireCareerAndCareerPath1_4'), t('ProfileQuestionnaireCareerAndCareerPath1_5'), t('ProfileQuestionnaireCareerAndCareerPath1_6'),
                                        t('ProfileQuestionnaireCareerAndCareerPath1_7'), t('ProfileQuestionnaireCareerAndCareerPath1_8'), t('ProfileQuestionnaireCareerAndCareerPath1_9'),
                                        t('ProfileQuestionnaireCareerAndCareerPath1_10'), t('ProfileQuestionnaireCareerAndCareerPath1_11')].map((option, index) => (
                                            <div key={index} className={`type ${educationalQualification === index + 1 ? 'selected' : ''}`} onClick={() => handleSelection('educationalQualification', index + 1)}>{option}</div>
                                        ))}
                                    </div>
                                </div>
                                <div className="frage">
                                    <p> {t('ProfileQuestionnaireCareerAndCareerPath2')}</p>
                                    <div className="onclick-boxes" id="employment_types">
                                        {[t('ProfileQuestionnaireCareerAndCareerPath2_1'), t('ProfileQuestionnaireCareerAndCareerPath2_2'), t('ProfileQuestionnaireCareerAndCareerPath2_3'),
                                        t('ProfileQuestionnaireCareerAndCareerPath2_4'), t('ProfileQuestionnaireCareerAndCareerPath2_5'), t('ProfileQuestionnaireCareerAndCareerPath2_6')].map((option, index) => (
                                            <div key={index} className={`type ${employmentType === index + 1 ? 'selected' : ''}`} onClick={() => handleSelection('employmentType', index + 1)}>{option}</div>
                                        ))}
                                    </div>
                                </div>
                                <div className="frage">
                                    <p> {t('ProfileQuestionnaireCareerAndCareerPath3')}</p>
                                    <div className="onclick-boxes" id="main_job">
                                        {[t('ProfileQuestionnaireCareerAndCareerPath3_1'), t('ProfileQuestionnaireCareerAndCareerPath3_2'), t('ProfileQuestionnaireCareerAndCareerPath3_3'),
                                        t('ProfileQuestionnaireCareerAndCareerPath3_4'), t('ProfileQuestionnaireCareerAndCareerPath3_5'), t('ProfileQuestionnaireCareerAndCareerPath3_6'),
                                        t('ProfileQuestionnaireCareerAndCareerPath3_7'), t('ProfileQuestionnaireCareerAndCareerPath3_8'), t('ProfileQuestionnaireCareerAndCareerPath3_9'),
                                        t('ProfileQuestionnaireCareerAndCareerPath3_10'), t('ProfileQuestionnaireCareerAndCareerPath3_11'), t('ProfileQuestionnaireCareerAndCareerPath3_12'),
                                        t('ProfileQuestionnaireCareerAndCareerPath3_13'), t('ProfileQuestionnaireCareerAndCareerPath3_14'), t('ProfileQuestionnaireCareerAndCareerPath3_15'),
                                        t('ProfileQuestionnaireCareerAndCareerPath3_16')].map((option, index) => (
                                            <div key={index} className={`type ${mainJob === index + 1 ? 'selected' : ''}`} onClick={() => handleSelection('mainJob', index + 1)}>{option}</div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </details>
                        <details className="fragebogen-part-wrapper" id="fragebogen-part-3"
                            style={{
                                '--icon-red': redCross ? `url(${redCross})` : '',
                                '--icon-green': greenCheck ? `url(${greenCheck})` : '',
                            }}>
                            <summary className={`summary-fragen ${leisureProgress === leisureMax ? "summary-gruen" : "summary-rot"}`}>{t('ProfileQuestionnaireLeisure')} ({leisureProgress}/{leisureMax})</summary>
                            <div class="fragen-wrapper">
                                <div className="slider-container" id="activitylvl">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireLeisure1')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireLeisure1_1'), t('ProfileQuestionnaireLeisure1_2'), t('ProfileQuestionnaireLeisure1_3'),
                                        t('ProfileQuestionnaireLeisure1_4'), t('ProfileQuestionnaireLeisure1_5'), t('ProfileQuestionnaireLeisure1_6'),
                                        t('ProfileQuestionnaireLeisure1_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`doing-option-${index}`}
                                                    name="outgoingradio"
                                                    checked={outgoing === index}
                                                    onChange={() => handleSelection('outgoing', index)} />
                                                <label htmlFor={`doing-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="slider-container" id="metime">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireLeisure2')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireLeisure2_1'), t('ProfileQuestionnaireLeisure2_2'), t('ProfileQuestionnaireLeisure2_3'),
                                        t('ProfileQuestionnaireLeisure2_4'), t('ProfileQuestionnaireLeisure2_5'), t('ProfileQuestionnaireLeisure2_6'),
                                        t('ProfileQuestionnaireLeisure2_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`couch-option-${index}`}
                                                    name="couchpotatoradio"
                                                    checked={couchpotato === index}
                                                    onChange={() => handleSelection('couchpotato', index)} />
                                                <label htmlFor={`couch-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="slider-container" id="personalEducation">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireLeisure3')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireLeisure3_1'), t('ProfileQuestionnaireLeisure3_2'), t('ProfileQuestionnaireLeisure3_3'),
                                        t('ProfileQuestionnaireLeisure3_4'), t('ProfileQuestionnaireLeisure3_5'), t('ProfileQuestionnaireLeisure3_6'),
                                        t('ProfileQuestionnaireLeisure3_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`personalEducation-option-${index}`}
                                                    name="personalEducationradio"
                                                    checked={personalEducation === index}
                                                    onChange={() => handleSelection('personalEducation', index)} />
                                                <label htmlFor={`personalEducation-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="frage">
                                    <p> {t('ProfileQuestionnaireLeisure4')}</p>
                                    <div className="onclick-boxes" id="hobbies">
                                        {[t('ProfileQuestionnaireLeisure4_1'), t('ProfileQuestionnaireLeisure4_2'), t('ProfileQuestionnaireLeisure4_3'),
                                        t('ProfileQuestionnaireLeisure4_4'), t('ProfileQuestionnaireLeisure4_5'), t('ProfileQuestionnaireLeisure4_6'),
                                        t('ProfileQuestionnaireLeisure4_7'), t('ProfileQuestionnaireLeisure4_8'), t('ProfileQuestionnaireLeisure4_9'),
                                        t('ProfileQuestionnaireLeisure4_10'), t('ProfileQuestionnaireLeisure4_11')].map((option, index) => (
                                            <div key={index} className={`type ${hobbies.includes(index + 1) ? 'selected' : ''}`} onClick={() => handleSelection('hobbies', index + 1)}>{option}</div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </details>
                        <details className="fragebogen-part-wrapper" id="fragebogen-part-4"
                            style={{
                                '--icon-red': redCross ? `url(${redCross})` : '',
                                '--icon-green': greenCheck ? `url(${greenCheck})` : '',
                            }}>
                            <summary className={`summary-fragen ${eveningProgress === eveningMax ? "summary-gruen" : "summary-rot"}`}> {t('ProfileQuestionnaireEvening')} ({eveningProgress}/{eveningMax})</summary>
                            <div className="fragen-wrapper">
                                <div className="slider-container" id="eveningBook">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireEvening1')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireEvening1_1'), t('ProfileQuestionnaireEvening1_2'), t('ProfileQuestionnaireEvening1_3'),
                                        t('ProfileQuestionnaireEvening1_4'), t('ProfileQuestionnaireEvening1_5'), t('ProfileQuestionnaireEvening1_6'),
                                        t('ProfileQuestionnaireEvening1_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`eveningBook-option-${index}`}
                                                    name="eveningBookradio"
                                                    checked={eveningBook === index}
                                                    onChange={() => handleSelection('eveningBook', index)} />
                                                <label htmlFor={`eveningBook-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="slider-container" id="eveningCulture">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireEvening2')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireEvening2_1'), t('ProfileQuestionnaireEvening2_2'), t('ProfileQuestionnaireEvening2_3'),
                                        t('ProfileQuestionnaireEvening2_4'), t('ProfileQuestionnaireEvening2_5'), t('ProfileQuestionnaireEvening2_6'),
                                        t('ProfileQuestionnaireEvening2_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`eveningCulture-option-${index}`}
                                                    name="eveningCultureradio"
                                                    checked={eveningCulture === index}
                                                    onChange={() => handleSelection('eveningCulture', index)} />
                                                <label htmlFor={`eveningCulture-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="slider-container" id="eveningFoodOrder">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireEvening3')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireEvening3_1'), t('ProfileQuestionnaireEvening3_2'), t('ProfileQuestionnaireEvening3_3'),
                                        t('ProfileQuestionnaireEvening3_4'), t('ProfileQuestionnaireEvening3_5'), t('ProfileQuestionnaireEvening3_6'),
                                        t('ProfileQuestionnaireEvening3_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`eveningFoodOrder-option-${index}`}
                                                    name="eveningFoodOrderradio"
                                                    checked={eveningFoodOrder === index}
                                                    onChange={() => handleSelection('eveningFoodOrder', index)} />
                                                <label htmlFor={`eveningFoodOrder-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="slider-container" id="eveningRestaurant">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireEvening3')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireEvening4_1'), t('ProfileQuestionnaireEvening4_2'), t('ProfileQuestionnaireEvening4_3'),
                                        t('ProfileQuestionnaireEvening4_4'), t('ProfileQuestionnaireEvening4_5'), t('ProfileQuestionnaireEvening4_6'),
                                        t('ProfileQuestionnaireEvening4_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`eveningRestaurant-option-${index}`}
                                                    name="eveningRestaurantradio"
                                                    checked={eveningRestaurant === index}
                                                    onChange={() => handleSelection('eveningRestaurant', index)} />
                                                <label htmlFor={`eveningRestaurant-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="slider-container" id="eveningCocking">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireEvening5')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireEvening5_1'), t('ProfileQuestionnaireEvening5_2'), t('ProfileQuestionnaireEvening5_3'),
                                        t('ProfileQuestionnaireEvening5_4'), t('ProfileQuestionnaireEvening5_5'), t('ProfileQuestionnaireEvening5_6'),
                                        t('ProfileQuestionnaireEvening5_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`eveningCocking-option-${index}`}
                                                    name="eveningCockingradio"
                                                    checked={eveningCocking === index}
                                                    onChange={() => handleSelection('eveningCocking', index)} />
                                                <label htmlFor={`eveningCocking-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="slider-container" id="eveningOutgoing">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireEvening6')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireEvening6_1'), t('ProfileQuestionnaireEvening6_2'), t('ProfileQuestionnaireEvening6_3'),
                                        t('ProfileQuestionnaireEvening6_4'), t('ProfileQuestionnaireEvening6_5'), t('ProfileQuestionnaireEvening6_6'),
                                        t('ProfileQuestionnaireEvening6_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`eveningOutgoing-option-${index}`}
                                                    name="eveningOutgoingradio"
                                                    checked={eveningOutgoing === index}
                                                    onChange={() => handleSelection('eveningOutgoing', index)} />
                                                <label htmlFor={`eveningOutgoing-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="slider-container" id="eveningHussling">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnaireEvening7')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnaireEvening7_1'), t('ProfileQuestionnaireEvening7_2'), t('ProfileQuestionnaireEvening7_3'),
                                        t('ProfileQuestionnaireEvening7_4'), t('ProfileQuestionnaireEvening7_5'), t('ProfileQuestionnaireEvening7_6'),
                                        t('ProfileQuestionnaireEvening7_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`eveningHussling-option-${index}`}
                                                    name="eveningHusslingradio"
                                                    checked={eveningHussling === index}
                                                    onChange={() => handleSelection('eveningHussling', index)} />
                                                <label htmlFor={`eveningHussling-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </details>
                        <details className="fragebogen-part-wrapper" id="fragebogen-part-5"
                            style={{
                                '--icon-red': redCross ? `url(${redCross})` : '',
                                '--icon-green': greenCheck ? `url(${greenCheck})` : '',
                            }}>
                            <summary className={`summary-fragen ${personalProgress === personalMax ? "summary-gruen" : "summary-rot"}`}> {t('ProfileQuestionnairePersonally')} ({personalProgress}/{personalMax})</summary>
                            <div class="fragen-wrapper">
                                <div className="slider-container" id="alkoholkonsum">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnairePersonally1')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnairePersonally1_1'), t('ProfileQuestionnairePersonally1_2'), t('ProfileQuestionnairePersonally1_3'),
                                        t('ProfileQuestionnairePersonally1_4'), t('ProfileQuestionnairePersonally1_5'), t('ProfileQuestionnairePersonally1_6'),
                                        t('ProfileQuestionnairePersonally1_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`alcohol-option-${index}`}
                                                    name="alcoholradio"
                                                    checked={alcohol === index}
                                                    onChange={() => handleSelection('alcohol', index)} />
                                                <label htmlFor={`alcohol-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="slider-container" id="rauchkonsum">
                                    <div className="question-container">
                                        <p> {t('ProfileQuestionnairePersonally2')}</p>
                                    </div>
                                    <div className="radio-buttons-container">
                                        {[t('ProfileQuestionnairePersonally2_1'), t('ProfileQuestionnairePersonally2_2'), t('ProfileQuestionnairePersonally2_3'),
                                        t('ProfileQuestionnairePersonally2_4'), t('ProfileQuestionnairePersonally2_5'), t('ProfileQuestionnairePersonally2_6'),
                                        t('ProfileQuestionnairePersonally2_7')].map((option, index) => (
                                            <div key={index} className="radio-option">
                                                <input
                                                    type="radio"
                                                    id={`smoking-option-${index}`}
                                                    name="smokingradio"
                                                    checked={smoking === index}
                                                    onChange={() => handleSelection('smoking', index)} />
                                                <label htmlFor={`smoking-option-${index}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="frage">
                                    <p> {t('ProfileQuestionnairePersonally3')}</p>
                                    <div className="modern-dropdown">
                                        <button
                                            className="dropdown-trigger"
                                            onClick={() => setChildrenDropdownOpen(!childrenDropdownOpen)}
                                        >
                                            {children ? [t('ProfileQuestionnairePersonally3_1'), t('ProfileQuestionnairePersonally3_2'), t('ProfileQuestionnairePersonally3_3')][children - 1] : "-"}
                                            <span className="dropdown-arrow">▾</span>
                                        </button>

                                        {childrenDropdownOpen && (
                                            <div className="dropdown-menu">
                                                {[t('ProfileQuestionnairePersonally3_1'), t('ProfileQuestionnairePersonally3_2'), t('ProfileQuestionnairePersonally3_3')].map((option, index) => (
                                                    <div key={option} onClick={() => {
                                                        handleSelection('children', index + 1);
                                                        setChildrenDropdownOpen(false);
                                                    }}>
                                                        {option}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="frage">
                                    <p> {t('ProfileQuestionnairePersonally4')}</p>
                                    <div className="modern-dropdown">
                                        <button
                                            className="dropdown-trigger"
                                            onClick={() => setAnimalsDropdownOpen(!animalsDropdownOpen)}
                                        >
                                            {animals ? [t('ProfileQuestionnairePersonally4_1'), t('ProfileQuestionnairePersonally4_2'), t('ProfileQuestionnairePersonally4_3')][animals - 1] : "-"}
                                            <span className="dropdown-arrow">▾</span>
                                        </button>

                                        {animalsDropdownOpen && (
                                            <div className="dropdown-menu">
                                                {[t('ProfileQuestionnairePersonally4_1'), t('ProfileQuestionnairePersonally4_2'), t('ProfileQuestionnairePersonally4_3')].map((option, index) => (
                                                    <div key={option} onClick={() => {
                                                        handleSelection('animals', index + 1);
                                                        setAnimalsDropdownOpen(false);
                                                    }}>
                                                        {option}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="frage">
                                    <p> {t('ProfileQuestionnairePersonally5')}</p>
                                    <div className="onclick-boxes" id="favoriteMusic">
                                        {[t('ProfileQuestionnairePersonally5_1'), t('ProfileQuestionnairePersonally5_2'), t('ProfileQuestionnairePersonally5_3'), t('ProfileQuestionnairePersonally5_4'),
                                        t('ProfileQuestionnairePersonally5_5'), t('ProfileQuestionnairePersonally5_6'), t('ProfileQuestionnairePersonally5_7'), t('ProfileQuestionnairePersonally5_8'),
                                        t('ProfileQuestionnairePersonally5_9'), t('ProfileQuestionnairePersonally5_10'), t('ProfileQuestionnairePersonally5_11'), t('ProfileQuestionnairePersonally5_12'),
                                        t('ProfileQuestionnairePersonally5_13')].map((option, index) => (
                                            <div key={index} className={`type ${favoriteMusic.includes(index + 1) ? 'selected' : ''}`} onClick={() => handleSelection('favoriteMusic', index + 1)}>{option}</div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </details>
                        <button className="save-button" onClick={handleSubmit}>{t('ProfileQuestionnaireSave')}</button>
                    </div>
                )}
                <div className="show-more-mid-wrapper">
                    <button className="show-more" onClick={() => setShowMoreMid(!showMoreMid)}>
                        {showMoreMid ? t('ProfileQuestionnaireLess') : t('ProfileQuestionnaireMore')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileQuestionnaire;